/* eslint-disable max-lines */
import { createApi } from '@reduxjs/toolkit/query/react';
import { type BenefitType } from '@strata/constants';
import { sortAsc } from '@strata/helper-functions';

import { REFERENCE_API_URL } from 'api/APIBase';
import { handleRTKQueryError } from 'services/Error';
import { getBaseQuery } from 'store/api/apiBaseQuery';

export interface PrescriberLookupEntry {
    prescriberNpi: string;
    firstName: string;
    lastName: string;
    telephoneNumber?: string;
    faxNumber?: string;
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    zip?: string;
    practiceNpi?: string;
}

export interface PracticeDetails {
    practiceNpi: string;
    practiceName: string;
    telephoneNumber?: string;
    faxNumber?: string;
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    zip?: string;
}

export interface ProductServiceCode {
    product_service_id?: string;
    codesystem: string;
    code: string;
    description?: string;
    dosage?: string;
    quantity_unit_of_measure?: string;
    strength?: string;
    strength_unit_of_measure?: string;
    is_brand?: boolean;
}

export interface ProductMapEntry {
    description?: string;
    brandDescription?: string;
    genericDescription?: string;
}

export interface ProductCodeOption {
    name: string;
    strength?: string;
    strength_unit_of_measure?: string;
    dosage?: string;
    quantity_unit_of_measure_code?: string;
}

export const referenceApi = createApi({
    baseQuery: getBaseQuery(REFERENCE_API_URL),
    reducerPath: 'referenceApi',
    endpoints: (build) => ({
        getSegments: build.query<{ segmentId: string; payerId: string; name: string }[], void>({
            query: () => 'payer_segments',
            transformResponse: (
                response: { segment_id: string; payer_id: string; segment_name: string }[],
            ) =>
                response
                    ?.map((segment) => ({
                        segmentId: segment.segment_id,
                        payerId: segment.payer_id,
                        name: segment.segment_name,
                    }))
                    .sort((a, b) => sortAsc(a.name, b.name)),
            transformErrorResponse: handleRTKQueryError('An error occurred retrieving payers.'),
        }),
        getProductsMap: build.query<Record<string, ProductMapEntry>, void>({
            query: () => '/productservices/summaryonly',
            transformResponse: (
                response: {
                    oid: string;
                    description?: string;
                    generic_product_description?: string;
                    brand_product_description?: string;
                }[],
            ) =>
                Object.fromEntries<ProductMapEntry>(
                    (response ?? []).map((product) => [
                        product.oid,
                        {
                            description: product.description,
                            brandDescription: product.brand_product_description,
                            genericDescription: product.generic_product_description,
                        },
                    ]),
                ),
            transformErrorResponse: handleRTKQueryError('An error occurred retrieving products.'),
        }),
        getProductsForSegment: build.query<
            {
                name: string;
                productServiceOid: string;
                brandProductDescription: string;
                genericProductDescription: string;
                isBrandDefault: boolean;
            }[],
            { segmentId: string }
        >({
            query: ({ segmentId }) => `product_benefit_configs/segments/${segmentId}`,
            transformResponse: (
                response: {
                    product_service_oid: string;
                    description: string;
                    brand_product_description: string;
                    generic_product_description: string;
                    is_brand_default: boolean;
                }[],
            ) =>
                response
                    ?.map((product) => ({
                        name: product.description,
                        productServiceOid: product.product_service_oid,
                        brandProductDescription: product.brand_product_description,
                        genericProductDescription: product.generic_product_description,
                        isBrandDefault: product.is_brand_default,
                    }))
                    // Dedupe products that have both benefit types
                    .filter(
                        (product, index, arr) =>
                            arr.findIndex(
                                (item) => item.productServiceOid === product.productServiceOid,
                            ) === index,
                    )
                    .sort((a, b) => sortAsc(a.name, b.name)),
        }),
        getDiagnoses: build.query<
            {
                name: string;
                oid: string;
                codes: {
                    code: string;
                    codesystem: string;
                    description: string;
                }[];
            }[],
            void
        >({
            query: () => 'diagnoses/',
            transformResponse: (
                response: {
                    oid: string;
                    description: string;
                    codes: {
                        code: string;
                        codesystem: string;
                        description: string;
                    }[];
                }[],
            ) =>
                response
                    ?.map((diagnosis) => ({
                        name: diagnosis.description,
                        oid: diagnosis.oid,
                        codes: diagnosis.codes,
                    }))
                    .sort((a, b) => sortAsc(a.name, b.name)),
        }),
        getPlacesOfService: build.query<{ code: string; name: string }[], void>({
            query: () => 'product_benefit_configs/places',
            transformResponse: (response: { code: string; description: string }[]) =>
                response
                    ?.map((option) => ({ code: option.code, name: option.description }))
                    .sort((a, b) => sortAsc(a.name, b.name)),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving places of service.',
            ),
        }),
        getParentSuppliers: build.query<
            { name: string; secondaryData: string; npi: string }[],
            void
        >({
            query: () => 'suppliers/parents',
            transformResponse: (response: { supplier_npi: string; organization_name: string }[]) =>
                response
                    ?.map((supplier) => ({
                        name: supplier.organization_name,
                        secondaryData: supplier.supplier_npi,
                        npi: supplier.supplier_npi,
                    }))
                    .sort((a, b) => sortAsc(a.name, b.name)),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving supplier options.',
            ),
        }),
        getPrescriberLookup: build.query<
            PrescriberLookupEntry[],
            { prescriberNpi?: string; firstName?: string; lastName?: string }
        >({
            query: ({ prescriberNpi, firstName, lastName }) => ({
                url: 'prescribers/lookup',
                params: {
                    npi: prescriberNpi,
                    first_name: firstName,
                    last_name: lastName,
                },
            }),
            transformResponse: (
                response: {
                    prescriber_npi: string;
                    first_name: string;
                    last_name: string;
                    telephone_number?: string;
                    fax_number?: string;
                    address_1?: string;
                    address_2?: string;
                    city?: string;
                    state?: string;
                    postal_code?: string;
                    practice_npi?: string;
                }[],
            ) =>
                response.map((prescriber) => ({
                    prescriberNpi: prescriber.prescriber_npi,
                    firstName: prescriber.first_name,
                    lastName: prescriber.last_name,
                    telephoneNumber: prescriber.telephone_number,
                    faxNumber: prescriber.fax_number,
                    address1: prescriber.address_1,
                    address2: prescriber.address_2,
                    city: prescriber.city,
                    state: prescriber.state,
                    zip: prescriber.postal_code?.trim(),
                    practiceNpi: prescriber.practice_npi,
                })),
        }),
        getPracticeDetails: build.query<PracticeDetails, string>({
            query: (practiceNpi) => `prescribers/practice/${practiceNpi}`,
            transformResponse: (response: {
                prescriber_npi: string;
                last_name: string;
                telephone_number?: string;
                fax_number?: string;
                address_1?: string;
                address_2?: string;
                city?: string;
                state?: string;
                postal_code?: string;
            }) => ({
                practiceNpi: response?.prescriber_npi,
                practiceName: response?.last_name,
                telephoneNumber: response?.telephone_number,
                faxNumber: response?.fax_number,
                address1: response?.address_1,
                address2: response?.address_2,
                city: response?.city,
                state: response?.state,
                zip: response?.postal_code?.trim(),
            }),
        }),
        getProductConfigForPayer: build.query<
            | {
                  additionalMedOptions: string[];
                  directionOptions: string[];
                  uomOptions: string[];
              }
            | undefined,
            { segmentId: string; productServiceOid: string; benefit: string }
        >({
            query: ({ segmentId, productServiceOid, benefit }) =>
                `product_benefit_configs/${segmentId}/${productServiceOid}/${benefit}`,
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving product configuration.',
            ),
            transformResponse: (response: {
                additional_med_options: string[] | null;
                direction_options: { option: string; strength_uom: string }[];
            }) =>
                response
                    ? {
                          additionalMedOptions: (response.additional_med_options ?? []).sort(
                              sortAsc,
                          ),
                          directionOptions: response.direction_options
                              .map(({ option }) => option)
                              .sort(sortAsc),
                          uomOptions: [
                              ...new Set(
                                  response.direction_options.map(
                                      ({ strength_uom }) => strength_uom,
                                  ),
                              ),
                          ].sort(sortAsc),
                      }
                    : undefined,
        }),
        getReferentProductCode: build.query<
            string,
            { productServiceOid: string; benefit: BenefitType }
        >({
            query: ({ productServiceOid, benefit }) => ({
                url: `productservices/${productServiceOid}/codes/referent`,
                params: { billing_type: benefit.toLocaleLowerCase() },
            }),
            transformResponse: (response: { code: string }) => response.code,
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving referent product code.',
            ),
        }),
        getProductCodes: build.query<ProductCodeOption[], { productServiceOid: string }>({
            query: ({ productServiceOid }) => ({
                url: `productservices/${productServiceOid}`,
            }),
            transformResponse: (response: { codes: ProductServiceCode[] }) =>
                response.codes
                    .filter((code) => code.codesystem === 'NDC')
                    .map((code) => ({
                        name: code.code,
                        strength: code.strength,
                        strength_unit_of_measure: code.strength_unit_of_measure,
                        quantity_unit_of_measure_code: code.quantity_unit_of_measure,
                        dosage: code.dosage,
                    })),
            transformErrorResponse: handleRTKQueryError('Unable to get list of NDCs for product.'),
        }),
    }),
});

export const {
    useGetSegmentsQuery,
    useGetProductsMapQuery,
    useGetProductsForSegmentQuery,
    useGetDiagnosesQuery,
    useGetPlacesOfServiceQuery,
    useGetParentSuppliersQuery,
    useLazyGetPrescriberLookupQuery,
    useLazyGetPracticeDetailsQuery,
    useGetProductConfigForPayerQuery,
    useLazyGetReferentProductCodeQuery,
    useGetProductCodesQuery,
} = referenceApi;
